import {
  Badge,
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EditIcon, TrashIcon } from "lucide-react";
import { RowActions } from "@/shared/ui/RowActions";
import type { ShopStaffListResult } from "../api/client";

type StaffRow = ShopStaffListResult["data"][number];

type ShopStaffTableProps = {
  data: StaffRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit: (staff: StaffRow) => void;
  onDelete: (staff: StaffRow) => void;
  canUpdate: boolean;
  canDelete: boolean;
};

export function ShopStaffTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onDelete,
  canUpdate,
  canDelete,
}: ShopStaffTableProps) {
  const columns: TanstackReactTable.ColumnDef<StaffRow>[] = [
    createSortableColumn<StaffRow>("userEmail", "ອີເມວ", { size: 120 }),
    createSortableColumn<StaffRow>("userName", "ຊື່", { size: 100 }),
    {
      id: "roleName",
      header: "ບົດບາດ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.roleName ?? "—"}
        </span>
      ),
      size: 100,
    },
    {
      id: "branchName",
      header: "ສາຂາ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.branchName ?? "—"}
        </span>
      ),
      size: 100,
    },
    {
      id: "isActive",
      header: "ສະຖານະ",
      cell: ({ row }) =>
        row.original.isActive ? (
          <span className="text-muted-foreground text-sm">ໃຊ້ງານຢູ່</span>
        ) : (
          <Badge variant="secondary">ບໍ່ໄດ້ໃຊ້ງານ</Badge>
        ),
      size: 80,
    },
    {
      id: "actions",
      size: 100,
      cell: ({ row }) => {
        const staff = row.original;
        const actions: {
          label: string;
          icon?: React.ReactNode;
          variant?: "destructive";
          onClick: () => void;
        }[] = [];
        if (canUpdate)
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="size-4" />,
            onClick: () => onEdit(staff),
          });
        if (canDelete)
          actions.push({
            label: "ລົບ",
            variant: "destructive",
            icon: <TrashIcon className="size-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ລົບພະນັກງານອອກຈາກຮ້ານ",
                description: `ລົບການມອບໝາຍທັງໝົດຂອງຜູ້ນີ້ໃນຮ້ານນີ້ (ທຸກສາຂາ/ບົດບາດ) — ບັນຊີຜູ້ໃຊ້ຍັງຢູ່ — "${staff.userName ?? staff.userEmail}"?`,
                actionText: "ລົບ",
                ActionProps: { variant: "destructive" },
              });
              if (ok) onDelete(staff);
            },
          });
        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <DataTable<StaffRow, unknown>
      noDataMessage="ຍັງບໍ່ມີພະນັກງານ. ກົດ ເພີ່ມພະນັກງານ ເພື່ອເລີ່ມຕົ້ນ."
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
